import React, { useMemo, useState } from 'react';
import {
  FaArrowRight,
  FaBoxes,
  FaBullhorn,
  FaCalculator,
  FaCashRegister,
  FaFileInvoiceDollar,
  FaGlobe,
  FaIndustry,
  FaShoppingCart,
  FaTasks,
  FaUsers,
  FaWarehouse,
} from 'react-icons/fa';
import {
  ODOO_CANVAS_LAYOUT_COMPACT,
  ODOO_CANVAS_LAYOUT_PHONE,
  ODOO_CANVAS_MODULES,
  ODOO_MODULE_LINKS,
  ODOO_MODULE_TILES,
} from '../data/odoo-modules.js';
import { useMediaQuery } from '../utils/useMediaQuery.js';

const MODULES = ODOO_CANVAS_MODULES;
const LINKS = ODOO_MODULE_LINKS;
const W = 1280;
const H = 720;

const sizeMap = {
  sm: { w: 132, h: 92, font: 10, label: 12 },
  md: { w: 152, h: 98, font: 10, label: 13 },
  lg: { w: 182, h: 112, font: 11, label: 15 },
  xl: { w: 204, h: 124, font: 11, label: 16 },
};

const compactSizeMap = {
  sm: { w: 78, h: 54, font: 7, label: 9 },
  md: { w: 88, h: 60, font: 7, label: 10 },
  lg: { w: 100, h: 66, font: 8, label: 11 },
  xl: { w: 110, h: 72, font: 8, label: 12 },
};

const phoneSizeMap = {
  sm: { w: 58, h: 38, font: 6, label: 7 },
  md: { w: 84, h: 48, font: 6, label: 8 },
  lg: { w: 94, h: 54, font: 7, label: 9 },
  xl: { w: 104, h: 60, font: 7, label: 10 },
};

const summaries = {
  crm: { kpi: 'Purpose', val: 'Leads & pipeline', flow: ['Lead', 'Qualify', 'Demo', 'Won'] },
  sales: { kpi: 'Purpose', val: 'Quotes to orders', flow: ['Quote', 'Approve', 'Send', 'Sign'] },
  acc: { kpi: 'Purpose', val: 'Finance & VAT', flow: ['Bill', 'Match', 'Approve', 'Post'] },
  inv: { kpi: 'Purpose', val: 'Stock control', flow: ['Receive', 'Putaway', 'Pick', 'Ship'] },
  mfg: { kpi: 'Purpose', val: 'Production', flow: ['Plan', 'Produce', 'QC', 'Pack'] },
  ec: { kpi: 'Purpose', val: 'Online sales', flow: ['Cart', 'Pay', 'Pick', 'Ship'] },
  web: { kpi: 'Purpose', val: 'Web presence', flow: ['Draft', 'Review', 'Publish', 'Index'] },
  hr: { kpi: 'Purpose', val: 'People & payroll', flow: ['Time', 'Approve', 'Pay', 'WPS'] },
  mkt: { kpi: 'Purpose', val: 'Campaigns', flow: ['Plan', 'Generate', 'Test', 'Launch'] },
  proj: { kpi: 'Purpose', val: 'Projects', flow: ['Brief', 'Build', 'Review', 'Ship'] },
  pos: { kpi: 'Purpose', val: 'Point of sale', flow: ['Sell', 'Sync', 'Reconcile', 'Close'] },
};

const moduleLabel = (key) => ODOO_MODULE_TILES.find((module) => module.key === key)?.label || key;
const moduleIcons = {
  crm: FaUsers,
  acc: FaCalculator,
  inv: FaWarehouse,
  mfg: FaIndustry,
  sales: FaFileInvoiceDollar,
  web: FaGlobe,
  ec: FaShoppingCart,
  hr: FaUsers,
  mkt: FaBullhorn,
  proj: FaTasks,
  pos: FaCashRegister,
};

const edgePoint = (from, to) => {
  const dx = to.cx - from.cx;
  const dy = to.cy - from.cy;
  const xScale = dx === 0 ? Infinity : from.w / 2 / Math.abs(dx);
  const yScale = dy === 0 ? Infinity : from.h / 2 / Math.abs(dy);
  const scale = Math.min(xScale, yScale);

  return {
    x: from.cx + dx * scale,
    y: from.cy + dy * scale,
  };
};

function linkPath(link, from, to) {
  if (link.route === 'pos-acc') {
    const start = { x: from.cx + from.w / 2, y: from.cy - from.h * 0.18 };
    const end = { x: to.cx - to.w / 2 - 12, y: to.cy + to.h * 0.14 };
    const laneY = Math.max(54, Math.min(start.y, end.y) - 100);
    const liftX = start.x + 120;
    return `M ${start.x} ${start.y} C ${liftX} ${start.y - 14}, ${liftX} ${laneY}, ${liftX + 96} ${laneY} L ${
      end.x - 64
    } ${laneY} C ${end.x - 30} ${laneY}, ${end.x - 46} ${end.y}, ${end.x} ${end.y}`;
  }

  if (link.route === 'pos-inv') {
    const start = { x: from.cx + from.w / 2, y: from.cy + from.h * 0.22 };
    const end = { x: to.cx - to.w / 2, y: to.cy + to.h * 0.12 };
    const laneY = Math.min(Math.max(start.y + 72, 450), end.y + 126);
    const laneX = start.x + (end.x - start.x) * 0.48;
    return `M ${start.x} ${start.y} C ${laneX} ${start.y + 24}, ${laneX} ${laneY}, ${laneX + 90} ${laneY} S ${
      end.x - 72
    } ${end.y + 10}, ${end.x} ${end.y}`;
  }

  if (link.from === 'inv' && link.to === 'mfg') {
    const start = { x: from.cx - from.w * 0.18, y: from.cy + from.h / 2 };
    const end = { x: to.cx - to.w * 0.3, y: to.cy - to.h / 2 };
    const laneX = Math.min(start.x - 68, end.x - 58);
    return `M ${start.x} ${start.y} C ${laneX} ${start.y + 18}, ${laneX} ${end.y - 34}, ${end.x} ${end.y}`;
  }

  if (link.from === 'mfg' && link.to === 'inv') {
    const start = { x: from.cx + from.w * 0.32, y: from.cy - from.h / 2 };
    const end = { x: to.cx + to.w * 0.32, y: to.cy + to.h / 2 };
    const laneX = Math.max(start.x + 70, end.x + 64);
    return `M ${start.x} ${start.y} C ${laneX} ${start.y - 26}, ${laneX} ${end.y + 24}, ${end.x} ${end.y}`;
  }

  if (link.from === 'sales' && link.to === 'acc') {
    const start = { x: from.cx + from.w * 0.22, y: from.cy - from.h / 2 };
    const end = { x: to.cx - to.w / 2 - 12, y: to.cy + to.h * 0.3 };
    return `M ${start.x} ${start.y} C ${start.x + 52} ${start.y - 58}, ${end.x - 52} ${end.y + 46}, ${end.x} ${end.y}`;
  }

  if (link.from === 'proj' && link.to === 'acc') {
    const start = { x: from.cx - from.w / 2, y: from.cy - from.h * 0.12 };
    const end = { x: to.cx + to.w / 2 + 12, y: to.cy + to.h * 0.24 };
    const laneX = Math.max(start.x - 96, end.x + 72);
    return `M ${start.x} ${start.y} C ${laneX} ${start.y - 16}, ${laneX} ${end.y + 18}, ${end.x} ${end.y}`;
  }

  if (link.from === 'hr' && link.to === 'acc') {
    const start = { x: from.cx + from.w / 2, y: from.cy - from.h * 0.28 };
    const end = { x: to.cx - to.w / 2 - 12, y: to.cy + to.h * 0.28 };
    const laneY = Math.max(start.y - 138, 96);
    return `M ${start.x} ${start.y} C ${start.x + 126} ${laneY}, ${end.x - 142} ${laneY}, ${end.x} ${end.y}`;
  }

  if (link.to === 'acc') {
    const start = edgePoint(from, to);
    const end = { x: to.cx - to.w / 2 - 12, y: to.cy + to.h * 0.32 };
    const laneX = Math.min(start.x + 90, end.x - 70);
    return `M ${start.x} ${start.y} C ${laneX} ${start.y}, ${laneX} ${end.y + 34}, ${end.x} ${end.y}`;
  }

  const start = edgePoint(from, to);
  const end = edgePoint(to, from);
  const mx = (start.x + end.x) / 2;
  return `M ${start.x} ${start.y} C ${mx} ${start.y}, ${mx} ${end.y}, ${end.x} ${end.y}`;
}

function Legend({ color, label }) {
  return (
    <span className="odoo-map-legend__item">
      <span style={{ '--legend-color': color }} />
      {label}
    </span>
  );
}

function ModuleNode({ module, active, relation, compact, phone, sizes, onClick }) {
  const size = sizes[module.size];
  const related = relation !== 'none';
  const Icon = moduleIcons[module.key] || FaBoxes;

  return (
    <button
      type="button"
      data-module-key={module.key}
      className={`odoo-map-node odoo-map-node--${relation}`}
      onClick={onClick}
      style={{
        '--module-color': module.color,
        '--module-x': module.x,
        '--module-y': module.y,
        '--module-w': `${size.w}px`,
        '--module-h': `${size.h}px`,
        '--module-label-size': `${size.label}px`,
        '--module-desc-size': `${size.font}px`,
        '--module-opacity': related ? 1 : compact ? 0.42 : 0.34,
        '--module-z': active ? 4 : related ? 3 : 2,
      }}
    >
      <span className="odoo-map-node__icon">
        <Icon aria-hidden="true" />
      </span>
      <strong>{module.label}</strong>
      {!phone && <em>{module.desc}</em>}
    </button>
  );
}

function FlowChip({ link, muted = false }) {
  return (
    <span className={muted ? 'odoo-flow-chip odoo-flow-chip--muted' : 'odoo-flow-chip'}>
      <span>{moduleLabel(link.from)}</span>
      <FaArrowRight />
      <span>{moduleLabel(link.to)}</span>
      <strong>{link.label}</strong>
    </span>
  );
}

function ModuleDetail({ moduleKey, links = [] }) {
  const module = ODOO_MODULE_TILES.find((item) => item.key === moduleKey);
  if (!module) return null;

  const data = summaries[moduleKey] || {};
  const outgoing = links.filter((link) => link.from === moduleKey);
  const incoming = links.filter((link) => link.to === moduleKey);
  const Icon = moduleIcons[moduleKey] || FaBoxes;

  return (
    <div className="odoo-map-detail">
      <div className="odoo-map-detail__module" style={{ '--module-color': module.color }}>
        <span>
          <Icon aria-hidden="true" />
        </span>
        <div>
          <strong>{module.label}</strong>
          <em>{module.desc}</em>
        </div>
      </div>
      <div className="odoo-map-detail__purpose">
        <span>{data.kpi}</span>
        <strong>{data.val}</strong>
      </div>
      <div className="odoo-map-detail__flows">
        {outgoing.length > 0 && (
          <div>
            <span>OUT</span>
            {outgoing.map((link) => (
              <FlowChip key={`${link.from}-${link.to}`} link={link} />
            ))}
          </div>
        )}
        {incoming.length > 0 && (
          <div>
            <span>IN</span>
            {incoming.map((link) => (
              <FlowChip key={`${link.from}-${link.to}`} link={link} muted />
            ))}
          </div>
        )}
        {links.length === 0 && (
          <div>
            <span>FLOW</span>
            {(data.flow || []).map((step) => (
              <strong key={step} className="odoo-flow-step">
                {step}
              </strong>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export function OdooModuleMap() {
  const [active, setActive] = useState('acc');
  const isPhoneCanvas = useMediaQuery('(max-width: 640px)');
  const isCompactCanvas = useMediaQuery('(max-width: 768px)');
  const nodeSizes = isPhoneCanvas ? phoneSizeMap : isCompactCanvas ? compactSizeMap : sizeMap;
  const canvasModules = useMemo(
    () =>
      isCompactCanvas
        ? ODOO_MODULE_TILES.map((tile, index) => ({
            ...tile,
            ...(isPhoneCanvas ? ODOO_CANVAS_LAYOUT_PHONE[index] : ODOO_CANVAS_LAYOUT_COMPACT[index]),
          }))
        : MODULES,
    [isCompactCanvas, isPhoneCanvas],
  );

  const positions = useMemo(() => {
    const map = {};
    canvasModules.forEach((module) => {
      const size = nodeSizes[module.size];
      map[module.key] = {
        cx: (parseFloat(module.x) / 100) * W,
        cy: (parseFloat(module.y) / 100) * H,
        w: size.w,
        h: size.h,
      };
    });
    return map;
  }, [canvasModules, nodeSizes]);

  const activeLinks = useMemo(() => LINKS.filter((link) => link.from === active || link.to === active), [active]);

  const relationByKey = useMemo(() => {
    const map = { [active]: 'active' };
    activeLinks.forEach((link) => {
      if (link.from === active) map[link.to] = map[link.to] === 'incoming' ? 'both' : 'outgoing';
      if (link.to === active) map[link.from] = map[link.from] === 'outgoing' ? 'both' : 'incoming';
    });
    return map;
  }, [active, activeLinks]);

  return (
    <div className="odoo-map-block">
      <div className={`odoo-map-canvas${isPhoneCanvas ? ' is-phone' : isCompactCanvas ? ' is-compact' : ''}`}>
        <div className="odoo-map-watermark">odoo</div>
        <svg className="odoo-map-lines" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMid meet" aria-hidden="true">
          <defs>
            <linearGradient id="flowOutV2" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#E42613" stopOpacity="0" />
              <stop offset="35%" stopColor="#E42613" stopOpacity="0.46" />
              <stop offset="100%" stopColor="#E42613" stopOpacity="1" />
            </linearGradient>
            <linearGradient id="flowInV2" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#017E84" stopOpacity="0" />
              <stop offset="35%" stopColor="#38AECC" stopOpacity="0.55" />
              <stop offset="100%" stopColor="#38AECC" stopOpacity="1" />
            </linearGradient>
            <marker id="arrowOutV2" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="8" markerHeight="8" orient="auto">
              <path d="M 0 0 L 10 5 L 0 10 z" fill="#E42613" />
            </marker>
            <marker id="arrowInV2" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="8" markerHeight="8" orient="auto">
              <path d="M 0 0 L 10 5 L 0 10 z" fill="#38AECC" />
            </marker>
          </defs>
          {activeLinks.map((link) => {
            const from = positions[link.from];
            const to = positions[link.to];
            if (!from || !to) return null;
            const outgoing = link.from === active;
            return (
              <path
                key={`${link.from}-${link.to}`}
                d={linkPath(link, from, to)}
                fill="none"
                stroke={outgoing ? 'url(#flowOutV2)' : 'url(#flowInV2)'}
                strokeWidth={isCompactCanvas ? 2 : 2.6}
                markerEnd={outgoing ? 'url(#arrowOutV2)' : 'url(#arrowInV2)'}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            );
          })}
        </svg>

        {canvasModules.map((module) => (
          <ModuleNode
            key={module.key}
            module={module}
            active={active === module.key}
            relation={relationByKey[module.key] || 'none'}
            compact={isCompactCanvas}
            phone={isPhoneCanvas}
            sizes={nodeSizes}
            onClick={() => setActive(module.key)}
          />
        ))}
      </div>

      <ModuleDetail moduleKey={active} links={activeLinks} />

      <div className="odoo-map-legend">
        <Legend color="#38AECC" label="Selected module" />
        <Legend color="#E42613" label="Sends data to" />
        <Legend color="#017E84" label="Receives data from" />
        <Legend color="#714B67" label="Outside this flow" />
      </div>
    </div>
  );
}
